import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery } from "../../../infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../infrastructure/routes/endpointsMetadata";
import { getAchievementToast } from "../../../infrastructure/utilities/toastUtility";
import {
  DistanceUnit,
  IToggleSetCompletedResult,
  IWorkoutPlanExerciseViewModel,
  IWorkoutPlanViewModel,
  IWorkoutSetViewModel,
  WorkoutExerciseType,
  WorkoutPlanDialogMode,
  WorkoutSection,
} from "../models/workoutPlanViewModel";
import { ISetExtraValues } from "./useSetRow";
import { MuscleGroup } from "../models/muscleGroups";
import { useRestTimerContext } from "../RestTimerContext";
import { formatAchievementToast } from "../../../../components/portal/myWorkouts/achievements/utils/achievementFormat";

interface IUseWorkoutPlanDialogArgs {
  eventId?: number;
  /** Open a standalone (event-less) workout directly by its plan id. */
  planId?: number;
  /**
   * The user whose history the workout is read against (ПРЕДИШНА, personal records), when
   * the page knows it — the admin user-profile workouts page. Sent with every request whose
   * answer depends on it. Without it the server decides: a regular user always gets their
   * own history, staff get the workout's single active registration (or nobody's when
   * several people are registered).
   */
  userId?: number;
  mode: WorkoutPlanDialogMode;
  isOpen: boolean;
}

const nextTempId = (() => {
  let counter = 0;
  return () => {
    counter += 1;
    return -(Date.now() + counter);
  };
})();

const nextClientKey = (() => {
  let counter = 0;
  return (prefix: "ex" | "set") => {
    counter += 1;
    return `${prefix}-${counter}-${Math.random().toString(36).slice(2, 8)}`;
  };
})();

const byPositionThenId = (
  a: { id: number; position: number },
  b: { id: number; position: number },
) => a.position - b.position || a.id - b.id;

/**
 * Merge a server-created entity over its optimistic placeholder. If the temp
 * row is still in the list it is swapped in place; if it vanished (a refetch or
 * a competing cache write replaced the list) the created row is inserted at
 * its server position instead of being dropped; if the list already holds the
 * real id (a refetch landed after the server persisted it) that copy is kept
 * and the temp row is removed.
 */
const upsertCreated = <T extends { id: number; position: number }>(
  items: T[],
  tempId: number,
  created: T,
): T[] => {
  if (items.some((i) => i.id === created.id)) {
    return items.filter((i) => i.id !== tempId).map((i) => (i.id === created.id ? created : i));
  }
  if (items.some((i) => i.id === tempId)) {
    return items.map((i) => (i.id === tempId ? created : i));
  }
  // Optimistic rows carry a guessed position that can collide with a real one
  // (a delete shifts the server's numbering), so break ties by id: the insert
  // never depends on the sort being stable.
  return [...items, created].sort(byPositionThenId);
};

/**
 * Undo of one optimistic write: given the plan as it is now and the snapshot
 * taken just before the write, return the plan with the failed mutation's own
 * fields put back — and nothing else touched.
 */
type PlanRollback = (
  current: IWorkoutPlanViewModel,
  snapshot: IWorkoutPlanViewModel,
) => IWorkoutPlanViewModel;

const mapSetInPlan = (
  plan: IWorkoutPlanViewModel,
  setId: number,
  map: (set: IWorkoutSetViewModel) => IWorkoutSetViewModel,
): IWorkoutPlanViewModel => ({
  ...plan,
  exercises: plan.exercises.map((e) => ({
    ...e,
    sets: e.sets.map((s) => (s.id === setId ? map(s) : s)),
  })),
});

const findSetInPlan = (plan: IWorkoutPlanViewModel, setId: number) => {
  for (const exercise of plan.exercises) {
    const set = exercise.sets.find((s) => s.id === setId);
    if (set) return { exercise, set };
  }
  return undefined;
};

/** Copy the fields `pick` names on one set back from the pre-write snapshot. */
const restoreSetFields = (
  setId: number,
  pick: (previous: IWorkoutSetViewModel) => Partial<IWorkoutSetViewModel>,
): PlanRollback => (current, snapshot) => {
  const previous = findSetInPlan(snapshot, setId);
  if (!previous) return current;
  return mapSetInPlan(current, setId, (s) => ({ ...s, ...pick(previous.set) }));
};

/** Same for one exercise; `pick` sees the pre-write row and the current one. */
const restoreExerciseFields = (
  exerciseId: number,
  pick: (
    previous: IWorkoutPlanExerciseViewModel,
    current: IWorkoutPlanExerciseViewModel,
  ) => Partial<IWorkoutPlanExerciseViewModel>,
): PlanRollback => (current, snapshot) => {
  const previous = snapshot.exercises.find((e) => e.id === exerciseId);
  if (!previous) return current;
  return {
    ...current,
    exercises: current.exercises.map((e) =>
      e.id === exerciseId ? { ...e, ...pick(previous, e) } : e,
    ),
  };
};

/** Put a set back where it was when the delete the user asked for failed. */
const restoreRemovedSet = (setId: number): PlanRollback => (current, snapshot) => {
  const previous = findSetInPlan(snapshot, setId);
  if (!previous) return current;
  return {
    ...current,
    exercises: current.exercises.map((e) =>
      e.id !== previous.exercise.id || e.sets.some((s) => s.id === setId)
        ? e
        : { ...e, sets: [...e.sets, previous.set].sort(byPositionThenId) },
    ),
  };
};

/**
 * Edits made to a row whose create is still queued or in flight. They are
 * applied to the cache immediately and replayed against the real id once the
 * create lands. Key order is the order the user made the edits (a re-edit of a
 * field moves it to the end), so the replay follows the user's sequence.
 */
interface IPendingSetEdit {
  kg?: number | null;
  reps?: number | null;
  resistanceKg?: number;
  speedKmh?: number;
  isCompleted?: boolean;
}

type PendingExerciseEdit = Partial<{
  notes: string;
  restTimerSeconds: number;
  useAutomaticTimer: boolean;
  supersetLetter: string | null;
  exerciseType: WorkoutExerciseType;
  muscleGroups: MuscleGroup[];
  isResistanceBand: boolean;
  section: WorkoutSection;
  distanceUnit: DistanceUnit;
}>;

const recordPendingEdit = <T extends object>(map: Map<number, T>, tempId: number, edit: T) => {
  const next: Record<string, unknown> = { ...(map.get(tempId) ?? {}) };
  // Re-recording a field moves it to the end so replay order matches the user's.
  for (const key of Object.keys(edit)) delete next[key];
  map.set(tempId, { ...next, ...edit } as T);
};

const takePendingEdit = <T,>(map: Map<number, T>, tempId: number): T | undefined => {
  const pending = map.get(tempId);
  map.delete(tempId);
  return pending;
};

/** Mirror of the server's UpdateSet/ToggleSetCompleted semantics on one row. */
const applySetEdit = (
  row: IWorkoutSetViewModel,
  edit: IPendingSetEdit,
  lockKg: boolean,
): IWorkoutSetViewModel => {
  let next = row;
  if ("kg" in edit || "reps" in edit) {
    // A trainee's weight is locked, so keep it; a trainer clearing the field
    // (kg === null) means 0.
    const nextKg = edit.kg ?? (lockKg ? row.kg : 0);
    next = { ...next, kg: nextKg, reps: edit.reps ?? 0 };
  }
  if (edit.resistanceKg !== undefined) next = { ...next, resistanceKg: edit.resistanceKg };
  if (edit.speedKmh !== undefined) next = { ...next, speedKmh: edit.speedKmh };
  if (edit.isCompleted !== undefined) {
    // Un-checking a set drops any badge it held; a re-derived list comes back
    // with the toggle response for the completed case.
    next = {
      ...next,
      isCompleted: edit.isCompleted,
      achievements: edit.isCompleted ? next.achievements : [],
    };
  }
  return next;
};

const buildExerciseEditRequest = (
  planExerciseId: number,
  key: keyof PendingExerciseEdit,
  edit: PendingExerciseEdit,
) => {
  const base = { method: "put" as const, disableSuccessToast: true };
  switch (key) {
    case "notes":
      return {
        ...base,
        endpoint: endpointsMetadata.workoutPlan.updateExerciseNotes,
        payload: { planExerciseId, notes: edit.notes },
      };
    case "restTimerSeconds":
      return {
        ...base,
        endpoint: endpointsMetadata.workoutPlan.updateExerciseRestTimer,
        payload: { planExerciseId, restTimerSeconds: edit.restTimerSeconds },
      };
    case "useAutomaticTimer":
      return {
        ...base,
        endpoint: endpointsMetadata.workoutPlan.updateExerciseUseAutomaticTimer,
        payload: { planExerciseId, useAutomaticTimer: edit.useAutomaticTimer },
      };
    case "supersetLetter":
      return {
        ...base,
        endpoint: endpointsMetadata.workoutPlan.updateExerciseSupersetLetter,
        payload: { planExerciseId, supersetLetter: edit.supersetLetter ?? "" },
      };
    case "exerciseType":
      return {
        ...base,
        endpoint: endpointsMetadata.workoutPlan.updateExerciseType,
        payload: { planExerciseId, exerciseType: edit.exerciseType },
      };
    case "muscleGroups":
      return {
        ...base,
        endpoint: endpointsMetadata.workoutPlan.updateExerciseMuscleGroups,
        payload: { planExerciseId, muscleGroups: edit.muscleGroups },
      };
    case "isResistanceBand":
      return {
        ...base,
        endpoint: endpointsMetadata.workoutPlan.updateExerciseResistanceBand,
        payload: { planExerciseId, isResistanceBand: edit.isResistanceBand },
      };
    case "section":
      return {
        ...base,
        endpoint: endpointsMetadata.workoutPlan.updateExerciseSection,
        payload: { planExerciseId, section: edit.section },
      };
    case "distanceUnit":
      return {
        ...base,
        endpoint: endpointsMetadata.workoutPlan.updateExerciseDistanceUnit,
        payload: { planExerciseId, distanceUnit: edit.distanceUnit },
      };
  }
};

/**
 * Where a mutation addressed by `id` should go: the row's current cache id and
 * whether its create is still pending (so the server call must wait for the real
 * id), or null when the row no longer exists (its create failed, or the user
 * removed it before the create landed) and there is nothing to update.
 */
type RowTarget = { id: number; pending: boolean } | null;

/**
 * List-level caches derived from a plan's sets/exercises (server-computed volume,
 * badges, lineage numbers, statistics). They are refreshed once per burst of edits
 * — when the mutation queue drains, or when the dialog closes — never per request,
 * so a rapid series of set edits cannot trigger a refetch storm of the My Workouts
 * list.
 */
const derivedListQueryKeys = [
  [endpointsMetadata.workoutPlan.getMyWorkouts],
  [endpointsMetadata.workoutPlan.getUserWorkoutsDetailed],
  [endpointsMetadata.workoutPlan.getMyWorkoutAchievements],
  [endpointsMetadata.workoutPlan.getUserWorkoutAchievements],
  [endpointsMetadata.workoutPlan.getMyWorkoutStatistics],
  [endpointsMetadata.workoutPlan.getUserWorkoutStatistics],
];

export const useWorkoutPlanDialog = ({
  eventId,
  planId,
  userId,
  mode,
  isOpen,
}: IUseWorkoutPlanDialogArgs) => {
  const isReadOnly = mode === "user";

  // Event-linked workouts open by event id (lazily creating the plan if needed);
  // standalone workouts (no event) open directly by plan id. A named user is part of the
  // key: the same plan read against another user's history is a different answer.
  const useEventPath = !!eventId;
  const planQueryKey = useMemo(
    () => [
      ...(useEventPath
        ? [endpointsMetadata.workoutPlan.getOrCreate, eventId]
        : [endpointsMetadata.workoutPlan.getById, planId]),
      ...(userId ? [userId] : []),
    ],
    [useEventPath, eventId, planId, userId],
  );
  const userQuery = userId ? `&userId=${userId}` : "";
  const requestEndpoint = useEventPath
    ? `${endpointsMetadata.workoutPlan.getOrCreate}?eventId=${eventId}${userQuery}`
    : `${endpointsMetadata.workoutPlan.getById}?planId=${planId}${userQuery}`;

  // Adds the named user to a request body whose answer depends on their history.
  const withUser = useCallback(
    <T extends object>(payload: T): T | (T & { userId: number }) =>
      userId ? { ...payload, userId } : payload,
    [userId],
  );
  const queryEnabled = isOpen && (useEventPath ? !!eventId : !!planId);

  const { data: plan, isLoading } = useApiQuery<IWorkoutPlanViewModel>({
    queryKey: planQueryKey,
    endpoint: requestEndpoint,
    enabled: queryEnabled,
    globalIsLoading: false,
    disableErrorToast: false,
  });

  const { mutateAsync } = useApiMutation<IWorkoutPlanExerciseViewModel | IWorkoutSetViewModel | unknown>();
  const queryClient = useQueryClient();

  // Temp → real id bookkeeping for creates still in flight. Removing a row that
  // is not yet persisted queues its delete behind the create; when that job runs
  // it resolves the temp id here. "failed" marks a create that never reached the
  // server, so there is nothing to delete.
  const tempToRealIdRef = useRef({
    set: new Map<number, number | "failed">(),
    exercise: new Map<number, number | "failed">(),
  });
  // Temp rows the user removed before their create landed: the create response
  // must not merge them back into the cache.
  const removedTempIdsRef = useRef({ set: new Set<number>(), exercise: new Set<number>() });
  // Edits to rows whose create has not landed yet (see IPendingSetEdit).
  const pendingSetEditsRef = useRef(new Map<number, IPendingSetEdit>());
  const pendingExerciseEditsRef = useRef(new Map<number, PendingExerciseEdit>());

  // The dialog stays mounted for the page's lifetime and is reused for every plan
  // the user opens, so none of the above may outlive the plan it belongs to.
  const resetPlanScopedState = useCallback(() => {
    tempToRealIdRef.current.set.clear();
    tempToRealIdRef.current.exercise.clear();
    removedTempIdsRef.current.set.clear();
    removedTempIdsRef.current.exercise.clear();
    pendingSetEditsRef.current.clear();
    pendingExerciseEditsRef.current.clear();
  }, []);

  // Serialise every server call for this plan. Optimistic cache writes stay
  // synchronous (the UI never waits), but the requests themselves run one at a
  // time, in the order they were enqueued, so a response can never race a later
  // request (or a refetch it triggers) and merge against a cache it no longer
  // matches. Enqueue order is not the order the user acted in: an edit made to a
  // row whose create is still in flight is held back and enqueued at the tail
  // once that create lands (see IPendingSetEdit), so it runs after everything
  // queued meanwhile.
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingCountRef = useRef(0);
  // The open-dialog reconcile (see the open effect below). `armed`: an invalidate
  // was issued, but the refetch it armed can still be cancelled by an optimistic
  // write. `owed`: one has to be (re-)issued as soon as the queue drains. The key
  // travels with the debt, so a job left over from a previously open plan cannot
  // make the drain refresh the wrong plan.
  const reconcileRef = useRef<{ owed: boolean; armed: boolean; queryKey: unknown[] | null }>({
    owed: false,
    armed: false,
    queryKey: null,
  });
  // A plan switch that happened mid-queue: the reset waits for the drain so a
  // queued delete can still resolve its temp id.
  const pendingPlanResetRef = useRef(false);
  // Set by every queued server write; cleared when the derived lists are refreshed.
  const listDirtyRef = useRef(false);

  const flushListInvalidation = useCallback(() => {
    if (!listDirtyRef.current) return;
    listDirtyRef.current = false;
    for (const queryKey of derivedListQueryKeys) {
      queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient]);

  const enqueue = useCallback(
    <T,>(fn: () => Promise<T>): Promise<T> => {
      pendingCountRef.current += 1;
      // Every queued job is a server write to this plan, so the lists derived
      // from it go stale; they are refreshed once the queue drains.
      listDirtyRef.current = true;
      const run = queueRef.current.then(fn, fn);
      // The chain itself always resolves so one failure never blocks the rest.
      queueRef.current = run.then(
        () => undefined,
        () => undefined,
      );
      return run.finally(() => {
        pendingCountRef.current -= 1;
        if (pendingCountRef.current !== 0) return;
        const reconcile = reconcileRef.current;
        if (reconcile.owed && reconcile.queryKey) {
          // An open-dialog refresh is still owed — it was either deferred while
          // requests were in flight or cancelled by an optimistic write. Re-issue
          // it now that the queue drained, against the plan it was armed for
          // (not necessarily the plan this job belonged to).
          reconcile.owed = false;
          reconcile.armed = true;
          queryClient.invalidateQueries({ queryKey: reconcile.queryKey });
        }
        if (pendingPlanResetRef.current) {
          pendingPlanResetRef.current = false;
          resetPlanScopedState();
        }
        flushListInvalidation();
      });
    },
    [queryClient, flushListInvalidation, resetPlanScopedState],
  );

  // Closing the dialog is when the user gets back to the list behind it, so make
  // sure it reflects the edits made here. A close while requests are still
  // queued leaves the refresh to the drain path above.
  useEffect(() => {
    if (isOpen || pendingCountRef.current > 0) return;
    flushListInvalidation();
  }, [isOpen, flushListInvalidation]);

  // Every time the dialog opens, ask the server for the latest plan. The
  // cached optimistic state shows instantly; the refetch reconciles in the
  // background — so a close/reopen always reflects the latest server truth
  // even if PrimeReact keeps the component mounted across visible toggles.
  // The reconcile is *owed* until a refetch actually gets to run: a reopen while
  // requests are still queued must not refetch mid-queue (the response would race
  // the pending ones), and the first optimistic write cancels the plan's in-flight
  // fetches — in both cases the drain re-issues it (see enqueue).
  useEffect(() => {
    if (!isOpen || !queryEnabled) return;
    reconcileRef.current.queryKey = planQueryKey;
    if (pendingCountRef.current > 0) {
      reconcileRef.current.owed = true;
      reconcileRef.current.armed = false;
      return;
    }
    reconcileRef.current.owed = false;
    reconcileRef.current.armed = true;
    queryClient.invalidateQueries({ queryKey: planQueryKey });
  }, [isOpen, queryEnabled, planQueryKey, queryClient]);

  // Point the dialog at another plan and its per-plan bookkeeping starts over.
  const planIdentity = `${useEventPath ? `event:${eventId}` : `plan:${planId}`}:user:${userId ?? ""}`;
  useEffect(() => {
    if (pendingCountRef.current > 0) {
      pendingPlanResetRef.current = true;
      return;
    }
    resetPlanScopedState();
  }, [planIdentity, resetPlanScopedState]);

  // Stable client-side keys for AnimatePresence — survive temp→real id swaps so rows
  // never remount when the server response replaces our optimistic entry.
  const keysRef = useRef({
    exercise: new Map<number, string>(),
    set: new Map<number, string>(),
  });

  const resolveExerciseKey = (id: number) => {
    let key = keysRef.current.exercise.get(id);
    if (!key) {
      key = nextClientKey("ex");
      keysRef.current.exercise.set(id, key);
    }
    return key;
  };

  const resolveSetKey = (id: number) => {
    let key = keysRef.current.set.get(id);
    if (!key) {
      key = nextClientKey("set");
      keysRef.current.set.set(id, key);
    }
    return key;
  };

  const transferExerciseKey = (fromId: number, toId: number) => {
    const key = keysRef.current.exercise.get(fromId);
    if (!key) return;
    keysRef.current.exercise.delete(fromId);
    keysRef.current.exercise.set(toId, key);
  };

  const transferSetKey = (fromId: number, toId: number) => {
    const key = keysRef.current.set.get(fromId);
    if (!key) return;
    keysRef.current.set.delete(fromId);
    keysRef.current.set.set(toId, key);
  };

  const updatePlanCache = useCallback(
    (updater: (current: IWorkoutPlanViewModel) => IWorkoutPlanViewModel) => {
      queryClient.setQueryData<IWorkoutPlanViewModel | undefined>(planQueryKey, (current) =>
        current ? updater(current) : current,
      );
    },
    [queryClient, planQueryKey],
  );

  // Optimistic write: cancel any in-flight refetch of the plan first so its
  // (older) response cannot land on top of the state the user just produced.
  const applyOptimistic = useCallback(
    (updater: (current: IWorkoutPlanViewModel) => IWorkoutPlanViewModel) => {
      // The cancel also aborts the refetch the open-dialog reconcile armed, so
      // hand that debt back to the drain instead of losing it.
      if (reconcileRef.current.armed) {
        reconcileRef.current.armed = false;
        reconcileRef.current.owed = true;
      }
      void queryClient.cancelQueries({ queryKey: planQueryKey });
      updatePlanCache(updater);
    },
    [queryClient, planQueryKey, updatePlanCache],
  );

  /**
   * Optimistic write, request, and — if the request fails — a rollback of the
   * fields this mutation owns.
   *
   * The whole plan is snapshotted before the write (it is one object reference,
   * so this is free), but the rollback puts back only what this mutation wrote,
   * picked out of that snapshot: restoring the entire snapshot would also undo
   * the optimistic writes of the other mutations queued meanwhile — with a
   * serialised queue there is always at least one of them in flight while a
   * request is pending.
   *
   * The rejection is swallowed on purpose: useApiMutation already toasts the
   * failure (a second toast would double up), and nothing in the dialog catches
   * what these handlers return, so a rethrow would only surface as an unhandled
   * promise rejection.
   */
  const runOptimistic = useCallback(
    async (
      apply: (current: IWorkoutPlanViewModel) => IWorkoutPlanViewModel,
      rollback: PlanRollback,
      send: () => Promise<unknown>,
    ): Promise<void> => {
      const snapshot = queryClient.getQueryData<IWorkoutPlanViewModel>(planQueryKey);
      applyOptimistic(apply);
      try {
        await send();
      } catch {
        if (snapshot) updatePlanCache((current) => rollback(current, snapshot));
      }
    },
    [queryClient, planQueryKey, applyOptimistic, updatePlanCache],
  );

  const sendRequest = useCallback(
    (request: Parameters<typeof mutateAsync>[0]) => enqueue(() => mutateAsync(request)),
    [enqueue, mutateAsync],
  );

  const resolveRealId = (kind: "set" | "exercise", id: number): number | undefined => {
    if (id >= 0) return id;
    const real = tempToRealIdRef.current[kind].get(id);
    return typeof real === "number" ? real : undefined;
  };

  // A temp id may outlive its create (a debounced input flush scheduled before
  // the id swap still addresses the temp row): once the create has landed the
  // edit simply targets the real id.
  const resolveRowTarget = (kind: "set" | "exercise", id: number): RowTarget => {
    if (id >= 0) return { id, pending: false };
    if (removedTempIdsRef.current[kind].has(id)) return null;
    const real = tempToRealIdRef.current[kind].get(id);
    if (real === undefined) return { id, pending: true };
    return real === "failed" ? null : { id: real, pending: false };
  };

  // A removed row stays on screen while it animates out, and a value typed into it is
  // flushed as it unmounts. Once the set has left the cache there is nothing to save:
  // a request for it would only come back "Сетът не беше открит". Either id may be the
  // one in the cache (a temp id whose create has landed resolves to the real one). A row
  // whose create is still pending is not checked: a refetch can drop it from the cache
  // before it is saved, and its edits are replayed once the create lands.
  const isSetInCache = useCallback(
    (...ids: (number | undefined)[]) => {
      const current = queryClient.getQueryData<IWorkoutPlanViewModel>(planQueryKey);
      return !!current && ids.some((id) => id !== undefined && !!findSetInPlan(current, id));
    },
    [queryClient, planQueryKey],
  );

  // A create request that records its outcome *inside* the queued job, so the
  // very next job (e.g. a queued delete of the same row) can already see it.
  const sendCreate = useCallback(
    <T extends { id: number }>(
      kind: "set" | "exercise",
      tempId: number,
      request: Parameters<typeof mutateAsync>[0],
    ): Promise<T> =>
      enqueue(() =>
        mutateAsync(request).then(
          (created) => {
            tempToRealIdRef.current[kind].set(tempId, (created as T).id);
            return created as T;
          },
          (error: unknown) => {
            tempToRealIdRef.current[kind].set(tempId, "failed");
            throw error;
          },
        ),
      ),
    [enqueue, mutateAsync],
  );

  const updateWorkoutPlanName = useCallback(
    async (name: string) => {
      if (isReadOnly || !plan) return;
      // Server stores trimmed-or-null; mirror that locally so the empty-name
      // placeholder reappears the moment the user clears the input.
      const trimmed = name.trim();
      const normalised: string | null = trimmed.length === 0 ? null : trimmed;
      await runOptimistic(
        (current) => ({ ...current, name: normalised }),
        (current, snapshot) => ({ ...current, name: snapshot.name }),
        () =>
          sendRequest({
            endpoint: endpointsMetadata.workoutPlan.updateName,
            method: "put",
            payload: { planId: plan.id, name: normalised },
            disableSuccessToast: true,
          }),
      );
    },
    [isReadOnly, plan, sendRequest, runOptimistic],
  );

  // Server calls for an exercise edit, one per field, in the recorded order.
  const sendExerciseEdits = useCallback(
    async (planExerciseId: number, edit: PendingExerciseEdit) => {
      for (const key of Object.keys(edit) as (keyof PendingExerciseEdit)[]) {
        await sendRequest(buildExerciseEditRequest(planExerciseId, key, edit));
      }
    },
    [sendRequest],
  );

  // Persist an exercise edit whose optimistic cache write has already happened:
  // straight away for a persisted row, or once its create lands for a temp row.
  const commitExerciseEdit = useCallback(
    async (target: NonNullable<RowTarget>, edit: PendingExerciseEdit) => {
      if (target.pending) {
        recordPendingEdit(pendingExerciseEditsRef.current, target.id, edit);
        return;
      }
      await sendExerciseEdits(target.id, edit);
    },
    [sendExerciseEdits],
  );

  // Runs the create for an optimistic exercise row and lands the response: the
  // edits made to the temp row meanwhile are merged over the created row before
  // it reaches the cache (so the temp→real id swap shows the user's values) and
  // then replayed against the real id.
  const createExercise = useCallback(
    async (tempId: number, request: Parameters<typeof mutateAsync>[0]) => {
      let created: IWorkoutPlanExerciseViewModel;
      try {
        created = await sendCreate<IWorkoutPlanExerciseViewModel>("exercise", tempId, request);
      } catch {
        pendingExerciseEditsRef.current.delete(tempId);
        keysRef.current.exercise.delete(tempId);
        updatePlanCache((current) => ({
          ...current,
          exercises: current.exercises.filter((e) => e.id !== tempId),
        }));
        return;
      }
      const pending = takePendingEdit(pendingExerciseEditsRef.current, tempId);
      if (removedTempIdsRef.current.exercise.has(tempId)) return; // its delete is queued
      transferExerciseKey(tempId, created.id);
      const merged = pending ? { ...created, ...pending } : created;
      // With edits still to persist the row is optimistic again, so an in-flight
      // refetch must not land on top of it.
      (pending ? applyOptimistic : updatePlanCache)((current) => ({
        ...current,
        exercises: upsertCreated(current.exercises, tempId, merged),
      }));
      if (!pending) return;
      // Failures are toasted by the mutation itself; the create has succeeded.
      await sendExerciseEdits(created.id, pending).catch(() => undefined);
    },
    [sendCreate, sendExerciseEdits, applyOptimistic, updatePlanCache],
  );

  const addExerciseFromCatalog = useCallback(
    async (
      catalogExerciseId: number,
      exerciseName: string,
      section: WorkoutSection = WorkoutSection.Workout,
    ) => {
      if (isReadOnly || !plan) return;
      const tempId = nextTempId();
      keysRef.current.exercise.set(tempId, nextClientKey("ex"));
      applyOptimistic((current) => ({
        ...current,
        exercises: [
          ...current.exercises,
          {
            id: tempId,
            exerciseId: catalogExerciseId,
            exerciseName,
            notes: null,
            position: current.exercises.length,
            restTimerSeconds: 0,
            useAutomaticTimer: true,
            supersetLetter: null,
            exerciseType: WorkoutExerciseType.KgReps,
            section,
            distanceUnit: DistanceUnit.Meters,
            isResistanceBand: false,
            muscleGroups: [],
            isSystemDefined: false,
            sets: [],
          },
        ],
      }));
      await createExercise(tempId, {
        endpoint: endpointsMetadata.workoutPlan.addExercise,
        method: "post",
        payload: withUser({ planId: plan.id, exerciseId: catalogExerciseId, section }),
        disableSuccessToast: true,
      });
    },
    [isReadOnly, plan, createExercise, applyOptimistic, withUser],
  );

  const addExerciseByName = useCallback(
    async (
      name: string,
      options?: { exerciseType: WorkoutExerciseType; isResistanceBand: boolean },
      section: WorkoutSection = WorkoutSection.Workout,
    ) => {
      if (isReadOnly || !plan || !name.trim()) return;
      const trimmed = name.trim();
      const tempId = nextTempId();
      keysRef.current.exercise.set(tempId, nextClientKey("ex"));
      applyOptimistic((current) => ({
        ...current,
        exercises: [
          ...current.exercises,
          {
            id: tempId,
            exerciseId: 0,
            exerciseName: trimmed,
            notes: null,
            position: current.exercises.length,
            restTimerSeconds: 0,
            useAutomaticTimer: true,
            supersetLetter: null,
            exerciseType: options?.exerciseType ?? WorkoutExerciseType.KgReps,
            section,
            distanceUnit: DistanceUnit.Meters,
            isResistanceBand: options?.isResistanceBand ?? false,
            muscleGroups: [],
            isSystemDefined: false,
            sets: [],
          },
        ],
      }));
      await createExercise(tempId, {
        endpoint: endpointsMetadata.workoutPlan.addExerciseByName,
        method: "post",
        payload: withUser({
          planId: plan.id,
          name: trimmed,
          section,
          ...(options ?? {}),
        }),
        disableSuccessToast: true,
      });
    },
    [isReadOnly, plan, createExercise, applyOptimistic, withUser],
  );

  const removeExercise = useCallback(
    async (planExerciseId: number) => {
      if (isReadOnly) return;
      const previous = plan?.exercises.find((e) => e.id === planExerciseId);
      const previousLineage = plan
        ? {
            lineageRootId: plan.lineageRootId,
            lineageNumber: plan.lineageNumber,
            lineageTotalCount: plan.lineageTotalCount,
            lineageRootName: plan.lineageRootName,
          }
        : null;
      applyOptimistic((current) => {
        const remaining = current.exercises.filter((e) => e.id !== planExerciseId);
        // Removing the last exercise resets the lineage on the server (so the next
        // template load can rebase the plan). Mirror that locally so the lineage
        // chip disappears immediately instead of after the next refetch.
        const becameEmpty = remaining.length === 0;
        return {
          ...current,
          exercises: remaining,
          lineageRootId: becameEmpty ? null : current.lineageRootId,
          lineageNumber: becameEmpty ? null : current.lineageNumber,
          lineageTotalCount: becameEmpty ? null : current.lineageTotalCount,
          lineageRootName: becameEmpty ? null : current.lineageRootName,
        };
      });
      keysRef.current.exercise.delete(planExerciseId);
      if (planExerciseId < 0) removedTempIdsRef.current.exercise.add(planExerciseId);
      try {
        // Queued behind a still-pending create of this exercise (see removeSet).
        const deleted = await enqueue(async () => {
          const realId = resolveRealId("exercise", planExerciseId);
          if (realId === undefined) return false;
          await mutateAsync({
            endpoint: `${endpointsMetadata.workoutPlan.removeExercise}?planExerciseId=${realId}`,
            method: "delete",
            disableSuccessToast: true,
          });
          return true;
        });
        if (!deleted) return;
        // Refresh the plan to pick up authoritative lineage info from the server.
        // Event-linked plans are cached under GetOrCreate, standalone ones under GetById.
        if (plan?.eventId) {
          queryClient.invalidateQueries({
            queryKey: [endpointsMetadata.workoutPlan.getOrCreate, plan.eventId],
          });
        } else if (plan) {
          queryClient.invalidateQueries({
            queryKey: [endpointsMetadata.workoutPlan.getById, plan.id],
          });
        }
        // The server may have shifted lineage numbers for sibling workouts (standalone
        // plans included), so drop the user-workouts cache the template dropdown reads from.
        queryClient.invalidateQueries({
          queryKey: [endpointsMetadata.workoutPlan.getUserWorkouts],
        });
      } catch {
        if (previous) {
          updatePlanCache((current) => ({
            ...current,
            exercises: [...current.exercises, previous].sort((a, b) => a.position - b.position),
            ...(previousLineage ?? {}),
          }));
        }
      }
    },
    [isReadOnly, plan, enqueue, mutateAsync, queryClient, applyOptimistic, updatePlanCache],
  );

  const updateExerciseNotes = useCallback(
    async (planExerciseId: number, notes: string) => {
      if (isReadOnly) return;
      const target = resolveRowTarget("exercise", planExerciseId);
      if (!target) return;
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) => (e.id === target.id ? { ...e, notes } : e)),
        }),
        restoreExerciseFields(target.id, (previous) => ({ notes: previous.notes })),
        () => commitExerciseEdit(target, { notes }),
      );
    },
    [isReadOnly, commitExerciseEdit, runOptimistic],
  );

  const updateExerciseRestTimer = useCallback(
    async (planExerciseId: number, restTimerSeconds: number) => {
      if (isReadOnly) return;
      const clamped = Math.max(0, Math.min(3600, Math.round(restTimerSeconds)));
      const target = resolveRowTarget("exercise", planExerciseId);
      if (!target) return;
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) =>
            e.id === target.id ? { ...e, restTimerSeconds: clamped } : e,
          ),
        }),
        restoreExerciseFields(target.id, (previous) => ({
          restTimerSeconds: previous.restTimerSeconds,
        })),
        () => commitExerciseEdit(target, { restTimerSeconds: clamped }),
      );
    },
    [isReadOnly, commitExerciseEdit, runOptimistic],
  );

  const updateExerciseUseAutomaticTimer = useCallback(
    async (planExerciseId: number, useAutomaticTimer: boolean) => {
      // Also a personal preference — both roles can flip it.
      const target = resolveRowTarget("exercise", planExerciseId);
      if (!target) return;
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) =>
            e.id === target.id ? { ...e, useAutomaticTimer } : e,
          ),
        }),
        restoreExerciseFields(target.id, (previous) => ({
          useAutomaticTimer: previous.useAutomaticTimer,
        })),
        () => commitExerciseEdit(target, { useAutomaticTimer }),
      );
    },
    [commitExerciseEdit, runOptimistic],
  );

  const updateExerciseSupersetLetter = useCallback(
    async (planExerciseId: number, supersetLetter: string | null) => {
      if (isReadOnly) return;
      // Normalise to a single uppercase letter or null, matching server validation.
      const trimmed = (supersetLetter ?? "").trim().toUpperCase();
      const normalised =
        trimmed.length === 1 && trimmed >= "A" && trimmed <= "Z" ? trimmed : null;
      const target = resolveRowTarget("exercise", planExerciseId);
      if (!target) return;
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) =>
            e.id === target.id ? { ...e, supersetLetter: normalised } : e,
          ),
        }),
        restoreExerciseFields(target.id, (previous) => ({
          supersetLetter: previous.supersetLetter,
        })),
        () => commitExerciseEdit(target, { supersetLetter: normalised }),
      );
    },
    [isReadOnly, commitExerciseEdit, runOptimistic],
  );

  const updateExerciseType = useCallback(
    async (planExerciseId: number, exerciseType: WorkoutExerciseType) => {
      if (isReadOnly) return;
      // Optimistically clear Kg/Reps on every set — that's exactly what the server
      // will do, so the UI reflects the cleared state immediately instead of
      // briefly showing the stale numbers in the wrong measurement system.
      const target = resolveRowTarget("exercise", planExerciseId);
      if (!target) return;
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) =>
            e.id === target.id
              ? {
                  ...e,
                  exerciseType,
                  sets: e.sets.map((s) => ({ ...s, kg: 0, reps: 0, speedKmh: null })),
                }
              : e,
          ),
        }),
        // The write owns the type *and* the kg/reps it cleared, so both come back;
        // a set added meanwhile keeps whatever it holds now.
        restoreExerciseFields(target.id, (previous, currentExercise) => {
          const previousSets = new Map(previous.sets.map((s) => [s.id, s]));
          return {
            exerciseType: previous.exerciseType,
            sets: currentExercise.sets.map((s) => {
              const before = previousSets.get(s.id);
              return before
                ? { ...s, kg: before.kg, reps: before.reps, speedKmh: before.speedKmh }
                : s;
            }),
          };
        }),
        () => commitExerciseEdit(target, { exerciseType }),
      );
    },
    [isReadOnly, commitExerciseEdit, runOptimistic],
  );

  const updateExerciseMuscleGroups = useCallback(
    async (planExerciseId: number, muscleGroups: MuscleGroup[]) => {
      if (isReadOnly) return;
      const target = resolveRowTarget("exercise", planExerciseId);
      if (!target) return;
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) =>
            e.id === target.id ? { ...e, muscleGroups } : e,
          ),
        }),
        restoreExerciseFields(target.id, (previous) => ({
          muscleGroups: previous.muscleGroups,
        })),
        () => commitExerciseEdit(target, { muscleGroups }),
      );
    },
    [isReadOnly, commitExerciseEdit, runOptimistic],
  );

  const updateExerciseResistanceBand = useCallback(
    async (planExerciseId: number, isResistanceBand: boolean) => {
      if (isReadOnly) return;
      const target = resolveRowTarget("exercise", planExerciseId);
      if (!target) return;
      // The flag lives on the shared exercise, so every card of it in this plan (a warm-up
      // and a main-part squat, say) changes together — and changes back together.
      const exerciseId = plan?.exercises.find((e) => e.id === target.id)?.exerciseId;
      const isSameExercise = (e: IWorkoutPlanExerciseViewModel) =>
        e.id === target.id || (exerciseId !== undefined && exerciseId > 0 && e.exerciseId === exerciseId);
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) =>
            isSameExercise(e) ? { ...e, isResistanceBand } : e,
          ),
        }),
        (current, snapshot) => ({
          ...current,
          exercises: current.exercises.map((e) => {
            const before = snapshot.exercises.find((s) => s.id === e.id);
            return isSameExercise(e) && before
              ? { ...e, isResistanceBand: before.isResistanceBand }
              : e;
          }),
        }),
        () => commitExerciseEdit(target, { isResistanceBand }),
      );
    },
    [isReadOnly, plan, commitExerciseEdit, runOptimistic],
  );

  const updateExerciseSection = useCallback(
    async (planExerciseId: number, section: WorkoutSection) => {
      if (isReadOnly) return;
      const target = resolveRowTarget("exercise", planExerciseId);
      if (!target) return;
      // The server drops the superset letter on a move (a superset stays inside one
      // section), so mirror that and give it back with the section on a failure.
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) =>
            e.id === target.id ? { ...e, section, supersetLetter: null } : e,
          ),
        }),
        restoreExerciseFields(target.id, (previous) => ({
          section: previous.section,
          supersetLetter: previous.supersetLetter,
        })),
        () => commitExerciseEdit(target, { section }),
      );
    },
    [isReadOnly, commitExerciseEdit, runOptimistic],
  );

  const updateExerciseDistanceUnit = useCallback(
    async (planExerciseId: number, distanceUnit: DistanceUnit) => {
      if (isReadOnly) return;
      const target = resolveRowTarget("exercise", planExerciseId);
      if (!target) return;
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) =>
            e.id === target.id ? { ...e, distanceUnit } : e,
          ),
        }),
        restoreExerciseFields(target.id, (previous) => ({
          distanceUnit: previous.distanceUnit,
        })),
        () => commitExerciseEdit(target, { distanceUnit }),
      );
    },
    [isReadOnly, commitExerciseEdit, runOptimistic],
  );

  // A completed set that beat a previous best comes back with its badges: stamp
  // the trophy on the row and celebrate. Shared by the direct toggle and the
  // deferred one replayed after a create lands.
  const applyToggleResult = useCallback(
    (
      setId: number,
      isCompleted: boolean,
      exerciseName: string,
      result: IToggleSetCompletedResult | undefined,
    ) => {
      const earned = result?.achievements ?? [];
      if (!isCompleted || earned.length === 0) return;
      // The user may have un-checked (or removed) the set while the request was
      // in flight: the record no longer stands, so neither stamp nor celebrate.
      const cached = queryClient.getQueryData<IWorkoutPlanViewModel>(planQueryKey);
      if (cached && !findSetInPlan(cached, setId)?.set.isCompleted) return;
      updatePlanCache((current) => ({
        ...current,
        exercises: current.exercises.map((e) => ({
          ...e,
          sets: e.sets.map((s) =>
            s.id === setId ? { ...s, achievements: earned.map((a) => a.type) } : s,
          ),
        })),
      }));
      getAchievementToast(formatAchievementToast(exerciseName, earned));
    },
    [updatePlanCache, queryClient, planQueryKey],
  );

  const sendToggleSetCompleted = useCallback(
    async (setId: number, isCompleted: boolean, exerciseName: string) => {
      const result = (await sendRequest({
        endpoint: endpointsMetadata.workoutPlan.toggleSetCompleted,
        method: "put",
        payload: withUser({ setId, isCompleted }),
        disableSuccessToast: true,
        // Completing/uncompleting a set changes the workout's total volume (My Workouts
        // cards) and may create or remove a personal record (Постижения tab) — both are
        // server-computed and refreshed once the mutation queue drains (see enqueue).
      })) as IToggleSetCompletedResult | undefined;
      applyToggleResult(setId, isCompleted, exerciseName, result);
    },
    [sendRequest, applyToggleResult, withUser],
  );

  const sendUpdateSet = useCallback(
    (setId: number, kg: number | null, reps: number | null, extra?: ISetExtraValues) =>
      sendRequest({
        endpoint: endpointsMetadata.workoutPlan.updateSet,
        method: "put",
        payload: { setId, kg, reps, ...(extra ?? {}) },
        disableSuccessToast: true,
      }),
    [sendRequest],
  );

  // Server calls for the edits recorded against a temp set, in recorded order:
  // kg/reps travel together in one UpdateSet, the completion flag in a toggle.
  const sendSetEdits = useCallback(
    async (setId: number, edit: IPendingSetEdit, exerciseName: string) => {
      let valuesSent = false;
      for (const key of Object.keys(edit) as (keyof IPendingSetEdit)[]) {
        if (key === "isCompleted") {
          await sendToggleSetCompleted(setId, edit.isCompleted ?? false, exerciseName);
        } else if (!valuesSent) {
          valuesSent = true;
          const extra: ISetExtraValues = {};
          if (edit.resistanceKg !== undefined) extra.resistanceKg = edit.resistanceKg;
          if (edit.speedKmh !== undefined) extra.speedKmh = edit.speedKmh;
          await sendUpdateSet(
            setId,
            edit.kg ?? null,
            edit.reps ?? null,
            Object.keys(extra).length > 0 ? extra : undefined,
          );
        }
      }
    },
    [sendToggleSetCompleted, sendUpdateSet],
  );

  const addSet = useCallback(
    async (planExerciseId: number) => {
      if (isReadOnly) return;
      if (planExerciseId < 0) return; // can't add to a not-yet-persisted exercise
      const tempId = nextTempId();
      keysRef.current.set.set(tempId, nextClientKey("set"));
      // Seed the new set from the last set *as the cache holds it right now*. A
      // just-flushed kg/reps edit (input blur → updateSet) has already landed in
      // the cache but its PUT may still be in flight, so the server's copy of the
      // last set can be stale. Sending the seed values explicitly makes the row
      // the server creates identical to the optimistic one, whatever order the
      // two requests are processed in.
      const cachedSets =
        queryClient
          .getQueryData<IWorkoutPlanViewModel>(planQueryKey)
          ?.exercises.find((e) => e.id === planExerciseId)?.sets ?? [];
      const lastSet = cachedSets[cachedSets.length - 1];
      const seed = lastSet
        ? {
            kg: lastSet.kg,
            reps: lastSet.reps,
            resistanceKg: lastSet.resistanceKg ?? null,
            speedKmh: lastSet.speedKmh ?? null,
          }
        : null;
      applyOptimistic((current) => ({
        ...current,
        exercises: current.exercises.map((e) => {
          if (e.id !== planExerciseId) return e;
          return {
            ...e,
            sets: [
              ...e.sets,
              {
                id: tempId,
                kg: seed?.kg ?? 0,
                reps: seed?.reps ?? 0,
                resistanceKg: seed?.resistanceKg ?? null,
                speedKmh: seed?.speedKmh ?? null,
                isCompleted: false,
                position: e.sets.length,
                previousKg: null,
                previousReps: null,
                previousResistanceKg: null,
                previousSpeedKmh: null,
                achievements: [],
              },
            ],
          };
        }),
      }));
      const params = new URLSearchParams({ planExerciseId: String(planExerciseId) });
      if (seed) {
        params.set("kg", String(seed.kg));
        params.set("reps", String(seed.reps));
        if (seed.resistanceKg !== null) params.set("resistanceKg", String(seed.resistanceKg));
        if (seed.speedKmh !== null) params.set("speedKmh", String(seed.speedKmh));
      }
      // The new set's ПРЕДИШНА comes from the named user's history.
      if (userId) params.set("userId", String(userId));
      let created: IWorkoutSetViewModel;
      try {
        created = await sendCreate<IWorkoutSetViewModel>("set", tempId, {
          endpoint: `${endpointsMetadata.workoutPlan.addSet}?${params.toString()}`,
          method: "post",
          disableSuccessToast: true,
        });
      } catch {
        pendingSetEditsRef.current.delete(tempId);
        keysRef.current.set.delete(tempId);
        updatePlanCache((current) => ({
          ...current,
          exercises: current.exercises.map((e) =>
            e.id === planExerciseId
              ? { ...e, sets: e.sets.filter((s) => s.id !== tempId) }
              : e,
          ),
        }));
        return;
      }
      const pending = takePendingEdit(pendingSetEditsRef.current, tempId);
      if (removedTempIdsRef.current.set.has(tempId)) return; // its delete is queued
      transferSetKey(tempId, created.id);
      // Edits typed into the temp row meanwhile are merged over the server row
      // before it reaches the cache, so the temp→real id swap (which resyncs the
      // row's inputs) shows the user's values, not the seeded ones; they are then
      // persisted against the real id. Until that lands the row is optimistic
      // again, so an in-flight refetch must not overwrite it.
      const merged = pending ? applySetEdit(created, pending, isReadOnly) : created;
      (pending ? applyOptimistic : updatePlanCache)((current) => ({
        ...current,
        exercises: current.exercises.map((e) =>
          e.id === planExerciseId ? { ...e, sets: upsertCreated(e.sets, tempId, merged) } : e,
        ),
      }));
      if (!pending) return;
      const exerciseName =
        queryClient
          .getQueryData<IWorkoutPlanViewModel>(planQueryKey)
          ?.exercises.find((e) => e.id === planExerciseId)?.exerciseName ?? "";
      // Failures are toasted by the mutation itself; the create has succeeded.
      await sendSetEdits(created.id, pending, exerciseName).catch(() => undefined);
    },
    [
      isReadOnly,
      userId,
      sendCreate,
      sendSetEdits,
      queryClient,
      planQueryKey,
      applyOptimistic,
      updatePlanCache,
    ],
  );

  const updateSet = useCallback(
    async (setId: number, kg: number | null, reps: number | null, extra?: ISetExtraValues) => {
      // A trainee (read-only mode) may adjust only the reps, and only on a
      // kg×reps exercise — never the weight, and never the seconds/metres of a
      // time- or distance-measured exercise. Trainers may edit every field.
      const target = resolveRowTarget("set", setId);
      if (!target || (!target.pending && !isSetInCache(setId, target.id))) return;
      if (isReadOnly) {
        const owningExercise = plan?.exercises.find((e) =>
          e.sets.some((s) => s.id === target.id),
        );
        if (
          !owningExercise ||
          owningExercise.exerciseType !== WorkoutExerciseType.KgReps
        ) {
          return;
        }
        kg = null; // the weight is locked for trainees
        extra = undefined; // and so is the band
      }
      const edit: IPendingSetEdit = { kg, reps, ...(extra ?? {}) };
      const apply = (current: IWorkoutPlanViewModel) =>
        mapSetInPlan(current, target.id, (s) => applySetEdit(s, edit, isReadOnly));
      if (target.pending) {
        applyOptimistic(apply);
        // The row's create is still in flight: persisted once its real id is known.
        recordPendingEdit(pendingSetEditsRef.current, target.id, edit);
        return;
      }
      await runOptimistic(
        apply,
        restoreSetFields(target.id, (previous) => ({
          kg: previous.kg,
          reps: previous.reps,
          resistanceKg: previous.resistanceKg,
          speedKmh: previous.speedKmh,
        })),
        () => sendUpdateSet(target.id, kg, reps, extra),
      );
    },
    [isReadOnly, plan, isSetInCache, sendUpdateSet, applyOptimistic, runOptimistic],
  );

  const removeSet = useCallback(
    async (setId: number) => {
      if (isReadOnly) return;
      // A temp id whose create has landed now names the real row in the cache.
      const knownRealId = resolveRealId("set", setId);
      if (!isSetInCache(setId, knownRealId)) return; // already removed
      keysRef.current.set.delete(setId);
      if (setId < 0) removedTempIdsRef.current.set.add(setId);
      await runOptimistic(
        (current) => ({
          ...current,
          exercises: current.exercises.map((e) => ({
            ...e,
            sets: e.sets.filter((s) => s.id !== setId && s.id !== knownRealId),
          })),
        }),
        restoreRemovedSet(knownRealId ?? setId),
        // Queued behind a still-pending create of this row, so a temp id resolves
        // to the real one by the time the job runs; a failed create leaves nothing
        // to delete.
        () =>
          enqueue(async () => {
            const realId = resolveRealId("set", setId);
            if (realId === undefined) return;
            await mutateAsync({
              endpoint: `${endpointsMetadata.workoutPlan.removeSet}?setId=${realId}`,
              method: "delete",
              disableSuccessToast: true,
            });
          }),
      );
    },
    [isReadOnly, isSetInCache, enqueue, mutateAsync, runOptimistic],
  );

  const restTimer = useRestTimerContext();
  const { start: startRestTimer, stop: stopRestTimer } = restTimer;

  const toggleSetCompleted = useCallback(
    async (setId: number, isCompleted: boolean) => {
      const target = resolveRowTarget("set", setId);
      if (!target || (!target.pending && !isSetInCache(setId, target.id))) return;
      // Find the owning exercise so we can fire its rest timer the moment a set
      // is marked complete (mirroring how trainer apps like Hevy behave).
      const owningExercise = plan?.exercises.find((e) =>
        e.sets.some((s) => s.id === target.id),
      );
      const edit: IPendingSetEdit = { isCompleted };
      const apply = (current: IWorkoutPlanViewModel) =>
        mapSetInPlan(current, target.id, (s) => applySetEdit(s, edit, isReadOnly));

      // Local-only behavior: start (or cancel) the countdown right away. The
      // server round-trip persists IsCompleted; the timer runs purely in-browser.
      // The user can opt out of auto-start via the "Use platform's automatic
      // timer" checkbox — in that case checking a set leaves the duration as
      // a passive preference instead of kicking the countdown.
      if (
        isCompleted &&
        owningExercise &&
        owningExercise.restTimerSeconds > 0 &&
        owningExercise.useAutomaticTimer
      ) {
        startRestTimer({
          planExerciseId: owningExercise.id,
          exerciseName: owningExercise.exerciseName,
          durationSeconds: owningExercise.restTimerSeconds,
        });
      } else if (!isCompleted) {
        // Un-checking a set cancels the timer it spawned, if any.
        stopRestTimer({ ifPlanExerciseId: owningExercise?.id });
      }

      if (target.pending) {
        applyOptimistic(apply);
        // The row's create is still in flight: persisted once its real id is known.
        recordPendingEdit(pendingSetEditsRef.current, target.id, edit);
        return;
      }
      await runOptimistic(
        apply,
        restoreSetFields(target.id, (previous) => ({
          isCompleted: previous.isCompleted,
          achievements: previous.achievements,
        })),
        () => sendToggleSetCompleted(target.id, isCompleted, owningExercise?.exerciseName ?? ""),
      );
    },
    [
      isReadOnly,
      plan,
      isSetInCache,
      sendToggleSetCompleted,
      startRestTimer,
      stopRestTimer,
      applyOptimistic,
      runOptimistic,
    ],
  );

  const loadTemplate = useCallback(
    async (templateId: number) => {
      if (isReadOnly || !plan) return;
      // No optimistic update — the server response is the source of truth for the
      // whole exercise tree (new ids, prefilled previous-set values, lineage info).
      try {
        const updated = (await sendRequest({
          endpoint: endpointsMetadata.workoutPlan.loadTemplate,
          method: "post",
          payload: withUser({ planId: plan.id, templateId }),
          disableSuccessToast: true,
        })) as IWorkoutPlanViewModel;
        updatePlanCache(() => updated);
        // The new workout becomes part of a lineage that may affect siblings'
        // numbering, so drop the workouts list cache the dropdown reads from.
        queryClient.invalidateQueries({
          queryKey: [endpointsMetadata.workoutPlan.getUserWorkouts],
        });
      } catch {
        // Server-side error toast already fires; nothing more to roll back.
      }
    },
    [isReadOnly, plan, sendRequest, queryClient, updatePlanCache, withUser],
  );

  const reorderExercises = useCallback(
    async (orderedIds: number[]) => {
      if (isReadOnly || !plan) return;
      const previousExercises = plan.exercises;
      // Reflect the new order immediately — the reorder dialog closes on save,
      // so the user sees the updated list right away.
      applyOptimistic((current) => {
        const byId = new Map(current.exercises.map((e) => [e.id, e]));
        const reordered = orderedIds
          .map((id, idx) => {
            const ex = byId.get(id);
            return ex ? { ...ex, position: idx } : null;
          })
          .filter((e): e is IWorkoutPlanExerciseViewModel => e !== null);
        return { ...current, exercises: reordered };
      });
      try {
        await sendRequest({
          endpoint: endpointsMetadata.workoutPlan.reorderExercises,
          method: "put",
          payload: { planId: plan.id, orderedExerciseIds: orderedIds },
          disableSuccessToast: true,
        });
      } catch {
        updatePlanCache((current) => ({ ...current, exercises: previousExercises }));
      }
    },
    [isReadOnly, plan, sendRequest, applyOptimistic, updatePlanCache],
  );

  const deleteWorkout = useCallback(async () => {
    if (!plan) return;
    await sendRequest({
      endpoint: `${endpointsMetadata.workoutPlan.delete}?planId=${plan.id}`,
      method: "delete",
      disableSuccessToast: true,
    });
    // Deleting an event-linked plan leaves the event itself untouched but the plan is
    // gone — drop its cached GetOrCreate result so the next open lazily recreates an
    // empty plan.
    if (plan.eventId) {
      queryClient.invalidateQueries({
        queryKey: [endpointsMetadata.workoutPlan.getOrCreate, plan.eventId],
      });
    }
    // Any plan (standalone included) takes part in lineage numbering, so drop the
    // template dropdown's workouts list, same as removeExercise does.
    queryClient.invalidateQueries({
      queryKey: [endpointsMetadata.workoutPlan.getUserWorkouts],
    });
  }, [plan, sendRequest, queryClient]);

  // Augment cached items with stable clientKey for AnimatePresence to key on.
  const exercises = useMemo<IWorkoutPlanExerciseViewModel[]>(() => {
    if (!plan) return [];
    const withKeys = plan.exercises.map((e) => ({
      ...e,
      clientKey: resolveExerciseKey(e.id),
      sets: e.sets.map((s) => ({ ...s, clientKey: resolveSetKey(s.id) })),
    }));
    // Rows the plan no longer holds (deleted, or belonging to a plan opened
    // earlier) would otherwise keep their key for as long as the page lives.
    const liveExerciseIds = new Set(plan.exercises.map((e) => e.id));
    const liveSetIds = new Set(plan.exercises.flatMap((e) => e.sets.map((s) => s.id)));
    for (const id of keysRef.current.exercise.keys()) {
      if (!liveExerciseIds.has(id)) keysRef.current.exercise.delete(id);
    }
    for (const id of keysRef.current.set.keys()) {
      if (!liveSetIds.has(id)) keysRef.current.set.delete(id);
    }
    return withKeys;
  }, [plan]);

  return {
    plan,
    isLoading,
    isReadOnly,
    mode,
    exercises,
    updateWorkoutPlanName,
    addExerciseFromCatalog,
    addExerciseByName,
    removeExercise,
    updateExerciseNotes,
    updateExerciseRestTimer,
    updateExerciseUseAutomaticTimer,
    updateExerciseSupersetLetter,
    updateExerciseType,
    updateExerciseMuscleGroups,
    updateExerciseResistanceBand,
    updateExerciseSection,
    updateExerciseDistanceUnit,
    addSet,
    updateSet,
    removeSet,
    toggleSetCompleted,
    loadTemplate,
    reorderExercises,
    deleteWorkout,
    restTimer,
  };
};

export type WorkoutPlanDialogState = ReturnType<typeof useWorkoutPlanDialog>;
export type WorkoutSetForRow = IWorkoutSetViewModel;
